import { apiHandler } from "../buyerApiHandler";

/**
 * ₹99 Store admin client.
 *
 * Talks straight to the Buyer API (REACT_APP_BUYER_BASE_URL) through
 * buyerApiHandler, which attaches x-api-key — the same path Offer Management
 * and the coupon campaigns use. The Seller API is not involved.
 *
 * A deal is one real menu item sold at a Tazty-funded price. The "was" price
 * is never sent from here: the Buyer API reads it live from the item, so the
 * store is always paid its actual menu price.
 */

export interface StoreDealPayload {
  store_id: number;
  item_id: number;
  deal_price: number;
  /** ₹ Tazty will spend on this deal per day. null = unlimited. */
  daily_budget?: number | null;
  /** Plates one customer may order per day. null = unlimited. */
  per_user_daily_limit?: number | null;
  max_qty_per_order?: number;
  ends_at?: string | null;
  sequence?: number;
  is_active?: boolean;
  created_by?: string;
  /** Seconds the card holds the photo before the video plays each loop. */
  media_photo_seconds?: number;
  /** Play the promo clip muted. */
  media_muted?: boolean;
  /** Repeat the photo→video cycle. */
  media_loop?: boolean;
}

export interface StoreDealRow {
  id: number;
  store_id: number;
  store_name: string | null;
  item_id: number;
  item_name: string | null;
  image_url: string | null;
  menu_price: number;
  deal_price: number;
  tazty_pays_per_plate: number;
  /** False when the menu price has fallen to or below the deal price. */
  is_price_valid: boolean;
  daily_budget: number | null;
  per_user_daily_limit: number | null;
  max_qty_per_order: number;
  ends_at: string | null;
  is_ended: boolean;
  sequence: number;
  is_active: boolean;
  /** The item's own photo (fallback when no custom image is set). */
  item_image_url: string | null;
  /** The deal's uploaded image override, if any. */
  custom_image_url: string | null;
  /** The deal's uploaded promo video, if any. */
  video_url: string | null;
  media_photo_seconds: number;
  media_muted: boolean;
  media_loop: boolean;
  today_spend: number;
  today_orders: number;
  total_spend: number;
  total_orders: number;
  created_by: string | null;
}

export interface StoreDealStoreOption {
  id: number;
  name: string;
  locality: string | null;
}

export interface StoreDealItemOption {
  id: number;
  name: string;
  menu_price: number;
  image_url: string | null;
}

const BASE = "/admin/store-deals";

export const getStoreDeals = (params?: {
  status?: "all" | "active" | "inactive";
  search?: string;
}) => apiHandler("get", BASE, undefined, { params });

export const getStoreDeal = (id: number) => apiHandler("get", `${BASE}/${id}`);

export const createStoreDeal = (payload: StoreDealPayload) =>
  apiHandler("post", BASE, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const updateStoreDeal = (id: number, payload: Partial<StoreDealPayload>) =>
  apiHandler("patch", `${BASE}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const setStoreDealStatus = (id: number, isActive: boolean) =>
  apiHandler("patch", `${BASE}/${id}/status`, { is_active: isActive }, {
    headers: { "Content-Type": "application/json" },
  });

export const getStoreDealStoreOptions = (search?: string) =>
  apiHandler("get", `${BASE}/options/stores`, undefined, {
    params: search ? { search } : undefined,
  });

export const getStoreDealItemOptions = (storeId: number, search?: string) =>
  apiHandler("get", `${BASE}/options/stores/${storeId}/items`, undefined, {
    params: search ? { search } : undefined,
  });

/** Card media — uploaded after the deal row exists, so it needs the deal id. */
export const uploadStoreDealImage = (id: number, file: File) => {
  const form = new FormData();
  form.append("image", file);
  return apiHandler("post", `${BASE}/${id}/image`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteStoreDealImage = (id: number) =>
  apiHandler("delete", `${BASE}/${id}/image`);

export const uploadStoreDealVideo = (id: number, file: File) => {
  const form = new FormData();
  form.append("video", file);
  return apiHandler("post", `${BASE}/${id}/video`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteStoreDealVideo = (id: number) =>
  apiHandler("delete", `${BASE}/${id}/video`);

/**
 * ₹99 Store display labels — editable so the whole buyer app can be renamed
 * (e.g. "Offer Zone") without an app release. Stored as app_settings keys.
 */
export interface StoreDealSection {
  name: string;
  tagline: string;
  home_title: string;
}

const SECTION_KEYS = {
  name: "STORE_DEAL_NAME",
  tagline: "STORE_DEAL_TAGLINE",
  home_title: "STORE_DEAL_HOME_TITLE",
};

const SECTION_DEFAULTS: StoreDealSection = {
  name: "₹99 Store",
  tagline: "Great Food. Just ₹99!",
  home_title: "Today's deals",
};

export const getStoreDealSection = async (): Promise<StoreDealSection> => {
  const res: any = await apiHandler("get", "/app-settings");
  const rows: any[] = res?.data?.data ?? res?.data ?? [];
  const find = (key: string, fallback: string) =>
    rows.find((r) => r?.key === key)?.value || fallback;
  return {
    name: find(SECTION_KEYS.name, SECTION_DEFAULTS.name),
    tagline: find(SECTION_KEYS.tagline, SECTION_DEFAULTS.tagline),
    home_title: find(SECTION_KEYS.home_title, SECTION_DEFAULTS.home_title),
  };
};

export const saveStoreDealSection = (section: StoreDealSection) => {
  const upsert = (key: string, value: string) =>
    apiHandler(
      "post",
      "/app-settings",
      { key, value, category: "store_deal" },
      { headers: { "Content-Type": "application/json" } },
    );
  return Promise.all([
    upsert(SECTION_KEYS.name, section.name),
    upsert(SECTION_KEYS.tagline, section.tagline),
    upsert(SECTION_KEYS.home_title, section.home_title),
  ]);
};

/**
 * ₹99 Store banner media — the photo and promo clip that play inside the orange
 * ₹99 Store card on the buyer Home screen. Stored by the Buyer API; the app
 * picks them up on its next load, no release needed.
 */
export interface StoreDealHeroMedia {
  hero_image_url: string | null;
  hero_video_url: string | null;
}

const heroOf = (res: any): StoreDealHeroMedia => {
  const d = res?.data?.data ?? res?.data ?? res ?? {};
  return {
    hero_image_url: d?.hero_image_url ?? null,
    hero_video_url: d?.hero_video_url ?? null,
  };
};

export const getStoreDealHero = async (): Promise<StoreDealHeroMedia> =>
  heroOf(await apiHandler("get", `${BASE}/hero`));

export const uploadStoreDealHeroImage = async (file: File): Promise<StoreDealHeroMedia> => {
  const form = new FormData();
  form.append("image", file);
  return heroOf(
    await apiHandler("post", `${BASE}/hero/image`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
};

export const deleteStoreDealHeroImage = async (): Promise<StoreDealHeroMedia> =>
  heroOf(await apiHandler("delete", `${BASE}/hero/image`));

export const uploadStoreDealHeroVideo = async (file: File): Promise<StoreDealHeroMedia> => {
  const form = new FormData();
  form.append("video", file);
  return heroOf(
    await apiHandler("post", `${BASE}/hero/video`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
};

export const deleteStoreDealHeroVideo = async (): Promise<StoreDealHeroMedia> =>
  heroOf(await apiHandler("delete", `${BASE}/hero/video`));
